import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';

import {
  Card,
  Tabs,
  TabsPanel,
  Button,
  ToastContainer
} from '@salesforce/design-system-react';
import { TabDisabled } from './images/illustrations/tab-disabled';

import { DataExtensionToast, ImportProfileWizard } from './';
import { UnsavedChangesModal } from '../../common/components';
import { withFlags } from '../../flags/Flags';

import {
  AttributeMappingPanel,
  ExclusionRulesPanel,
  SelectionRulesPanel,
  VarietyRulesPanel
} from '../components/';

import './Rules.scss';

class Rules extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedIndex: undefined
    };
  }

  labels() {
    const { formatMessage } = this.props.intl;
    const i18nKey = 'components.ecs.rules';
    const labels = {};

    // include variety in the labels even though it might not get displayed
    const tabs = ['mapping', 'exclusion', 'selection', 'variety'];
    tabs.forEach(rule => {
      labels[rule] = formatMessage({ id: `${i18nKey}.${rule}.tab` });
    });
    return labels;
  }

  filteredProfileAttributes(flags) {
    if (flags.numberRules) {
      return this.props.profileAttributes;
    } else {
      return (
        this.props.profileAttributes?.filter(
          attr => attr.data_type !== 'decimal' || attr.data_type !== 'number'
        ) || []
      );
    }
  }

  cancelRules() {
    this.props.actions.revertFatigueRules();
    this.props.actions.revertExclusionRules();
    this.props.actions.revertClassifierRules();
    this.props.actions.revertVarietyRules();
  }

  cancelRulesAndGoBack() {
    this.cancelRules();
    this.props.history.goBack();
  }

  openUnsavedChangesModal(index) {
    const { name, unsavedChangesMessage } = this.props.modalData;
    const data = {
      unsavedChangesMessage,
      index
    };
    return this.props.blockNavCriteria
      ? this.props.actions.openModal(name, data)
      : this.cancelRules();
  }

  attributeMappingTab = (data, labels) => (
    <TabsPanel label={labels.mapping}>
      {!this.props.profileAttributes.length
        ? this.tabNotEnabled('attribute')
        : this.attributeMappingPanel(data)}
    </TabsPanel>
  );

  attributeMappingPanel = data => {
    return withFlags(flags => (
      <AttributeMappingPanel
        actions={this.props.actions}
        assetKeys={this.props.assetAttributes?.map(attr => attr.name)}
        profileKeys={this.filteredProfileAttributes(flags)?.map(
          attr => attr.name
        )}
        rules={data.rules}
        loadedRules={data.loaded}
        history={this.props.history}
        modals={this.props.modals}
        openUnsavedChangesModal={this.openUnsavedChangesModal.bind(this)}
      />
    ));
  };

  exclusionRulesTab = (data, labels) => (
    <TabsPanel label={labels.exclusion}>
      {!this.props.profileAttributes.length
        ? this.tabNotEnabled('exclusion')
        : this.exclusionRulesPanel(data)}
    </TabsPanel>
  );

  exclusionRulesPanel = data => {
    return withFlags(flags => (
      <ExclusionRulesPanel
        actions={this.props.actions}
        assetAttributes={this.props.assetAttributes}
        profileAttributes={this.filteredProfileAttributes(flags)}
        rules={data.rules}
        loadedRules={data.loaded}
        history={this.props.history}
        openUnsavedChangesModal={this.openUnsavedChangesModal.bind(this)}
        modals={this.props.modals}
      />
    ));
  };

  // TODO: duplicated code in ProfileAttributeList
  openProfileImportWizard() {
    const importConfiguration = this.props.importConfiguration?.data
      ?.import_configuration;

    if (!this.props.dataExtensions?.data?.data_extensions) {
      this.props.actions.getDataExtensions();
    }

    if (importConfiguration?.source_info) {
      const sourceInfo = importConfiguration?.source_info;
      const dataExtension = {
        ...sourceInfo,
        customer_key: sourceInfo.customer_de_key,
        sendable_data_extension_field: { name: sourceInfo.subscriber_key }
      };

      const profileAttributes = this.profileAttributes();

      // Load DE details if we have a previous import
      this.props.actions.getDataExtensionColumns(dataExtension);

      this.props.actions.openModal('profileImport', {
        step1: { completed: true, dataExtension: dataExtension },
        step2: {
          completed: true,
          schedule: {
            ...importConfiguration.schedule,
            importRecurring: importConfiguration?.schedule?.sync_days > 0
          },
          notification_info: importConfiguration?.notification_info || {}
        },
        step3: {
          selectedAttributes: profileAttributes
        },
        step4: { completed: true },
        step5: { profileAttributes: profileAttributes }
      });
    } else {
      this.props.actions.openModal('profileImport', {
        step1: { completed: false }
      });
    }
  }

  tabNotEnabled = tab => {
    const { formatMessage } = this.props.intl;
    const consumerAttributesHelpLink =
      'https://help.salesforce.com/articleView?id=mc_pb_map_asset_attributes_consumer_attributes.htm&type=5';
    const exclusionRulesHelpLink =
      'https://help.salesforce.com/articleView?id=mc_pb_set_exclusion_rules.htm&type=5';
    const profilesHelpLink =
      'https://help.salesforce.com/articleView?id=mc_pb_sync_your_consumer_profile_data_extension.htm&type=5';

    return withFlags(flags => (
      <div className="slds-grid--align-center slds-grid">
        <div className="slds-col slds-m-top_x-large">
          <div className="slds-illustration slds-illustration_small">
            <TabDisabled />

            <div className="slds-text-longform dashboard-no-data-container">
              <h3 className="slds-text-heading_medium">
                <FormattedMessage id="components.ecs.rules.profileMissingHeader" />
              </h3>
              <h3 className="slds-text-heading_xsmall">
                <FormattedMessage
                  id={
                    tab === 'attribute'
                      ? 'components.ecs.rules.mapping.disabled'
                      : 'components.ecs.rules.exclusion.disabled'
                  }
                  values={{
                    link: (
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        href={
                          tab === 'attribute'
                            ? consumerAttributesHelpLink
                            : exclusionRulesHelpLink
                        }
                      >
                        {formatMessage({
                          id: 'components.ecs.nocatalog.helpLinkText'
                        })}
                      </a>
                    )
                  }}
                />
              </h3>
              <h3 className="slds-text-heading_xsmall">
                <FormattedMessage
                  id={
                    tab === 'attribute'
                      ? 'components.ecs.rules.mapping.usageCondition'
                      : 'components.ecs.rules.exclusion.usageCondition'
                  }
                  values={{
                    link: (
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        href={profilesHelpLink}
                      >
                        {formatMessage({
                          id: 'components.ecs.nocatalog.helpLinkText'
                        })}
                      </a>
                    )
                  }}
                />
              </h3>

              <Button
                id="upload-button"
                label={formatMessage({
                  id: 'components.ecs.rules.profileMissingCTAButton'
                })}
                variant="brand"
                onClick={this.openProfileImportWizard.bind(this)}
              />
              <ImportProfileWizard
                actions={this.props.actions}
                modals={this.props.modals}
                attributes={this.props.attributes}
                dataExtensions={this.props.dataExtensions}
                rules={this.props.rules}
                importConfiguration={this.props.importConfiguration}
                flags={flags}
                maxAttributes={30}
              />
            </div>
          </div>
        </div>
      </div>
    ));
  };

  selectionRulesTab = (data, labels) => (
    <TabsPanel label={labels.selection}>
      <SelectionRulesPanel
        actions={this.props.actions}
        assetTypes={this.props.assetTypes}
        rules={data.rules}
        loadedRules={data.loaded}
        history={this.props.history}
        openUnsavedChangesModal={this.openUnsavedChangesModal.bind(this)}
        modals={this.props.modals}
      />
    </TabsPanel>
  );

  varietyRulesTab = (data, labels) => (
    <TabsPanel label={labels.variety}>
      <VarietyRulesPanel
        actions={this.props.actions}
        assetAttributes={this.props.assetAttributes}
        rules={data.rules}
        loadedRules={data.loaded}
        history={this.props.history}
        openUnsavedChangesModal={this.openUnsavedChangesModal.bind(this)}
        modals={this.props.modals}
      />
    </TabsPanel>
  );

  setSelectedTabIndex(index) {
    this.setState({
      selectedIndex: index
    });
  }

  selectTab(nextTabIndex) {
    if (this.props.blockNavCriteria) {
      this.openUnsavedChangesModal(nextTabIndex);
      return false;
    }
    this.setSelectedTabIndex(nextTabIndex);
  }

  advancedRules = (data, labels) =>
    withFlags(flags => (
      <Tabs
        defaultSelectedIndex={this.props.profileAttributes.length ? 0 : 2}
        onSelect={nextTabIndex => this.selectTab(nextTabIndex)}
        selectedIndex={this.state.selectedIndex}
      >
        {this.attributeMappingTab(data, labels)}
        {this.exclusionRulesTab(data, labels)}
        {this.selectionRulesTab(data, labels)}
        {this.varietyRulesTab(data, labels)}
      </Tabs>
    ));

  render() {
    const { data } = this.props.rules;
    return (
      <div>
        <Card className="rules-card">
          {this.advancedRules(data, this.labels())}
        </Card>
        <UnsavedChangesModal
          modalName="unsavedChangesModal"
          cancelRules={this.cancelRules.bind(this)}
          cancelRulesAndGoBack={this.cancelRulesAndGoBack.bind(this)}
          setSelectedTabIndex={this.setSelectedTabIndex.bind(this)}
          actions={this.props.actions}
          modals={this.props.modals}
        />
        <ToastContainer>
          <DataExtensionToast
            dataExtensions={this.props.dataExtensions}
            actions={this.props.actions}
            toast={this.props.toast}
          />
        </ToastContainer>
      </div>
    );
  }
}

Rules.propTypes = {
  actions: PropTypes.shape({
    getRules: PropTypes.func,
    openModal: PropTypes.func,
    saveRules: PropTypes.func
  }),
  assetKeys: PropTypes.array,
  assetTypes: PropTypes.array,
  dataExtensions: PropTypes.object,
  assetAttributes: PropTypes.array,
  profileAttributes: PropTypes.array,
  rules: PropTypes.object,
  modals: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object,
  imports: PropTypes.object.isRequired,
  toast: PropTypes.object.isRequired
};

export default injectIntl(Rules);
